import * as React from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete'
import { assetsApi, inspectionsApi } from '../../api-interface';
import { AssetDocumentReturnType, AssetReminderReturnType, AssetResponse, DocumentAIAnalysis, HydratedAssetAuditEvent } from '../../api';
import { Typography, Tooltip, Box, useTheme, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, TextField, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid } from '@mui/material';
import UnstyledLink from '../../components/common/UnstyledLink/UnstyledLink';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DownloadIcon from '@mui/icons-material/Download';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AssetInfoChip from './AssetInfoChip';
import HelpIcon from '@mui/icons-material/Help';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TenantContext } from '../..';
import { useSnackbar } from 'notistack';
import { GridRowsProp, GridColDef, getGridSingleSelectOperators, GridDownloadIcon } from '@mui/x-data-grid';
import StyledDataGrid, { stringFilterOperators } from '../../components/common/StyledDataGrid/StyledDataGrid';
import EditIcon from '@mui/icons-material/Edit';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import UploadDocumentModal from './components/UploadDocumentModal';
import { documentTypeLabels } from '../../components/common/documentTypeLabels';
import { reminderTypeLabels } from '../../components/common/reminderTypeLabels';
import AddReminderModal from './components/AddReminderModal';
import HeaderTableCell from '../../components/common/StyledTableCell/StyledTableCell';


export async function loader(tenant_id, asset_id) {
  const assets = await assetsApi(tenant_id).getAssetsAssetsGet();
  const asset = assets.data.find((x) => x.asset.id == asset_id)
  const analyses = await assetsApi(tenant_id).getAssetAnalysesAssetsAssetIdAnalysesGet(asset_id)
  const documents = await assetsApi(tenant_id).getAssetDocumentsAssetsAssetIdDocumentsGet(asset_id)
  const reminders = await assetsApi(tenant_id).getAssetRemindersAssetsAssetIdRemindersGet(asset_id)
  const audit_events = await assetsApi(tenant_id).getAssetAuditEventsAssetsAssetIdAuditEventsGet(asset_id)
  const ret = { "analyses": analyses.data, "asset": asset, "documents": documents.data, "reminders": reminders.data, "audit_events": audit_events.data };
  return ret;
}


const AssetInfo = () => {
  const data = useLoaderData() as any;
  const analyses = data["analyses"] as DocumentAIAnalysis[];
  const documents = data["documents"] as AssetDocumentReturnType[];
  const audit_events = data["audit_events"] as HydratedAssetAuditEvent[];
  const asset = data["asset"] as AssetResponse;
  const reminders = data["reminders"] as AssetReminderReturnType[];
  const address_sub_arr = [asset.asset.address_2, asset.asset.city, asset.asset.county, asset.asset.postcode].filter(x => x)
  const asset_sub_joined = address_sub_arr.join(", ")
  const tenant_id = React.useContext(TenantContext)
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate()

  const asset_compliant = asset.complaint
  const next_inspection_due = asset.most_recent_validation ? new Date(String(asset.most_recent_validation?.next_inspection_due)) : null
  const next_inspection_overdue = next_inspection_due ? new Date() > new Date(String(asset.most_recent_validation?.next_inspection_due)) : true

  const inspectionRow = (inspection: DocumentAIAnalysis) => {
    return {
      id: inspection.id,
      inspection_date: inspection.inspection_date ? new Date(String(inspection.inspection_date)) : null,
      engineer: inspection.engineer_name ? String(inspection.engineer_name) : "",
      next_inspection_due: inspection.next_inspection_due ? new Date(String(inspection.next_inspection_due)) : null,
      outcome: inspection.outcome == "pass" ? "Compliant" : inspection.outcome == "warning" ? "Warning" : "Needs Attention",
      analysis_id: inspection.id
    }
  }


  const documentcolumns: GridColDef[] = [
    { field: 'document_name', headerName: 'Document Name', flex: 1, minWidth: 170, headerClassName: 'table-header', filterOperators: stringFilterOperators },
    {
      field: 'document_type',
      headerName: 'Document Type',
      flex: 1,
      minWidth: 170,
      headerClassName: 'table-header',
      filterOperators: stringFilterOperators,
      renderCell: (params) => documentTypeLabels[params.value] || params.value
    },
    { field: 'filename', headerName: 'File Name', flex: 1, minWidth: 170, headerClassName: 'table-header', filterOperators: stringFilterOperators },
    {
      field: 'expiry', headerName: 'Expiry Date', flex: 1, minWidth: 170, type: 'date', headerClassName: 'table-header', renderCell: (params) => {
        return params.value ? params.value.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : "-"
      }
    },
    {
      field: 'created', headerName: 'Created', flex: 1, minWidth: 170, type: 'date', headerClassName: 'table-header', renderCell: (params) => {
        return params.value?.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      }
    },
    {
      // uploaded by
      field: 'uploaded_by', headerName: 'Uploaded By', flex: 1, minWidth: 170, headerClassName: 'table-header', filterOperators: stringFilterOperators
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerClassName: 'table-header',
      flex: 1,
      minWidth: 100,
      getActions: (params) => [
        <Tooltip title="Download Document"><DownloadIcon sx={{ verticalAlign: 'middle', cursor: 'pointer' }}
          onClick={
            async () => {
              try {
                const file = await assetsApi(tenant_id).downloadAssetDocumentAssetsAssetIdDocumentsDocumentIdDownloadGet(asset.asset.id, params.row.id, { responseType: "blob" });
                var link = document.createElement('a');
                link.href = window.URL.createObjectURL(file.data);
                var fileName = params.row.filename;
                link.download = fileName;
                link.click();
              } catch (error) {
                enqueueSnackbar("Error downloading file: " + error, { variant: "error" })
              }
            }
          } /></Tooltip>,
        <Tooltip title="Delete Document">
          <DeleteIcon onClick={async () => {
            if (window.confirm("Are you sure you want to remove this document?")) {
              try {
                await assetsApi(tenant_id).deleteAssetDocumentAssetsAssetIdDocumentsDocumentIdDelete(asset.asset.id, params.row.id)
                enqueueSnackbar("Document deleted", { variant: "success" })
              } catch (e) {
                enqueueSnackbar("Error deleting document: " + e, { variant: "error" })
              }
            }
            navigate("")
          }} sx={{ ml: 1, verticalAlign: 'middle', cursor: 'pointer' }} />
        </Tooltip>
      ],
    }
  ]
  const documentrows: GridRowsProp = documents.map((doc) => {
    return {
      id: doc.id,
      document_name: doc.document_name,
      document_type: doc.document_type,
      created: new Date(doc.upload_date),
      uploaded_by: doc.uploaded_by_username,
      filename: doc.filename,
      expiry: doc.expiry ? new Date(String(doc.expiry)) : null
    }
  });

  const lgsrcolumns: GridColDef[] = [
    {
      field: 'inspection_date', headerName: 'LGSR Inspection Date', flex: 1, minWidth: 170, type: 'date', headerClassName: 'table-header', renderCell: (params) => {
        return params.value?.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      }
    },
    { field: 'engineer', headerName: 'Engineer', flex: 1, minWidth: 150, headerClassName: 'table-header', filterOperators: stringFilterOperators },
    {
      field: 'next_inspection_due', headerName: 'Next LGSR Inspection Due', headerClassName: 'table-header', flex: 1, minWidth: 150, type: 'date', renderCell: (params) => {
        return params.value ? params.value.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : <HelpIcon sx={{ color: theme.palette.text.disabled, verticalAlign: 'middle' }}></HelpIcon>
      }
    },
    {
      field: 'outcome', headerName: 'LGSR Outcome', flex: 1, minWidth: 170, headerClassName: 'table-header', type: 'singleSelect', valueOptions: ['Compliant', 'Warning', 'Needs Attention'], filterOperators: getGridSingleSelectOperators(), renderCell: (params) => {
        return params.value === "Compliant" ?
          <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.success.main}><TaskAltIcon fontSize="inherit" /> Compliant</Typography>
          : params.value === "Warning" ? <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.warning.main}><ErrorOutlineIcon fontSize="inherit" /> Warning</Typography>
            : <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.error.main}><ErrorOutlineIcon fontSize="inherit" /> Needs Attention</Typography>
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerClassName: 'table-header',
      flex: 1,
      minWidth: 100,
      getActions: (params) => [
        <UnstyledLink to={"/dashboard/" + tenant_id + "/inspections/" + params.row.analysis_id + "/validations"}><Tooltip title="Verifications"><ChecklistIcon sx={{ verticalAlign: 'middle' }} /></Tooltip></UnstyledLink>,
        <Tooltip title="Delete LGSR Inspection">
          <DeleteIcon onClick={async () => {
            if (window.confirm("Are you sure you want to remove this inspection?")) {
              try {
                await inspectionsApi(tenant_id).deleteAnalysisInspectionsAnalysisIdDelete(params.row.id)
                enqueueSnackbar("LGSR Inspection deleted", { variant: "success" })
              } catch (e) {
                enqueueSnackbar("Error deleting inspection: " + e, { variant: "error" })
              }

            }
            navigate("")
          }} sx={{ ml: 1, verticalAlign: 'middle', cursor: 'pointer' }} />
        </Tooltip>
      ],
    }
  ];
  const lgsrrows: GridRowsProp = analyses.map(inspectionRow);

  const reminderColumns: GridColDef[] = [
    // Reminder type
    {
      field: 'type',
      headerName: 'Reminder Type',
      flex: 1,
      minWidth: 170,
      headerClassName: 'table-header',
      filterOperators: stringFilterOperators,
      renderCell: (params) => reminderTypeLabels[params.value] || params.value
    },
    {
      field: 'reminder_date',
      headerName: 'Reminder Date',
      flex: 1,
      minWidth: 170,
      headerClassName: 'table-header',
      type: 'date',
      renderCell: (params) => {
        return params.value ? params.value.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : "-"
      }
    },
    {
      field: 'created',
      headerName: 'Created',
      flex: 1,
      minWidth: 170,
      type: 'date',
      headerClassName: 'table-header',
      renderCell: (params) => {
        return params.value?.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      }
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      flex: 1,
      minWidth: 170,
      headerClassName: 'table-header',
      filterOperators: stringFilterOperators
    },
    // Notes
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 2,
      minWidth: 150,
      headerClassName: 'table-header',
      filterOperators: stringFilterOperators
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerClassName: 'table-header',
      flex: 1,
      minWidth: 100,
      getActions: (params) => [
        <Tooltip title="Delete Reminder">
          <DeleteIcon onClick={async () => {
            if (window.confirm("Are you sure you want to remove this reminder?")) {
              try {
                await assetsApi(tenant_id).deleteAssetReminderAssetsAssetIdRemindersReminderIdDelete(asset.asset.id, params.row.id)
                enqueueSnackbar("Reminder deleted", { variant: "success" })
              } catch (e) {
                enqueueSnackbar("Error deleting reminder: " + e, { variant: "error" })
              }
            }
            navigate("")
          }} sx={{ ml: 1, verticalAlign: 'middle', cursor: 'pointer' }} />
        </Tooltip>
      ],
    }
  ];
  const reminderRows: GridRowsProp = reminders.map((reminder) => {
    return {
      id: reminder.id,
      type: reminder.reminder_type,
      reminder_date: new Date(reminder.reminder_date),
      created: new Date(reminder.created),
      created_by: reminder.created_by_username,
      notes: reminder.notes
    }
  });



  const theme = useTheme();
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const handleUploadOpen = () => setUploadOpen(true);
  const handleUploadClose = () => setUploadOpen(false);

  const [addReminderOpen, setAddReminderOpen] = React.useState(false);
  const handleAddReminderOpen = () => setAddReminderOpen(true);
  const handleAddReminderClose = () => setAddReminderOpen(false);

  // Schema and form for editing address (copied from AssetAdd)
  const schema = yup.object({
    address_1: yup.string().required(),
    address_2: yup.string(),
    city: yup.string(),
    county: yup.string(),
    postcode: yup.string().required(),
  }).required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      address_1: asset.asset.address_1,
      address_2: typeof asset.asset.address_2 === 'string' ? asset.asset.address_2 : '',
      city: asset.asset.city,
      county: asset.asset.county,
      postcode: asset.asset.postcode,
    }
  });


  return (
    <Box display={"flex"} flexDirection={"column"} gap={5}>
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">Asset Overview </Typography>
      </Box>
      <Box display={"flex"} flexDirection={"row"} gap={2}>
        <Box flexGrow={1}>
          <Typography variant="h5" fontWeight={"bold"}>{asset.asset.address_1}</Typography>
          <Typography>{asset_sub_joined}</Typography>
          <Typography><b>UPRN: </b>{asset.asset.uprn}</Typography>
        </Box>
      </Box>
      <Grid container spacing={2}>

        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6 }}
        >
          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>Asset Status</Typography>}
            right={<Typography align={"right"} fontSize={"1.25rem"} color={asset_compliant == "pass" ? theme.palette.success.main : asset_compliant == "warning" ? theme.palette.warning.main : theme.palette.error.main} fontWeight={"bold"}>
              {asset_compliant == "pass" ? <TaskAltIcon fontSize='inherit' /> : <ErrorOutlineIcon fontSize='inherit' />} {asset_compliant == "pass" ? "Compliant" : asset_compliant == "warning" ? "Warning" : "Needs Attention"}
            </Typography>}
            bgcolor={asset_compliant == "pass" ? theme.palette.successBackground.main : asset_compliant == "warning" ? theme.palette.warningBackground.main : theme.palette.errorBackground.main}
            borderColor={asset_compliant == "pass" ? theme.palette.successOutline.main : asset_compliant == "warning" ? theme.palette.warningOutline.main : theme.palette.errorOutline.main}
          />
        </Grid>
        <Grid
          container
          spacing={2}
          size={{ 'xs': 12, 'sm': 6 }}
        >
          <AssetInfoChip
            left={<Typography fontWeight={500} fontSize={"1.25rem"}>Next LGSR Inspection Due</Typography>}
            right={<Typography align={"right"}  fontSize={"1.25rem"} color={next_inspection_overdue ? theme.palette.error.main : theme.palette.success.main} fontWeight={"bold"}>
              {next_inspection_due ? next_inspection_due.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : <HelpIcon></HelpIcon>}
            </Typography>}
            bgcolor={next_inspection_overdue ? theme.palette.errorBackground.main : theme.palette.successBackground.main}
            borderColor={next_inspection_overdue ? theme.palette.errorOutline.main : theme.palette.successOutline.main}
          />
        </Grid>
      </Grid>
      <Box sx={{
        width: '100%'
      }} >
        <Typography variant="h5">LGSR Inspections</Typography>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StyledDataGrid rows={lgsrrows} columns={lgsrcolumns} showToolbar />
        </div>
      </Box>
      <Box sx={{
        width: '100%'
      }} >

        <Box display="flex" justifyContent="space-between" alignItems={"flex-end"}>
          <Typography variant="h5">Documents</Typography>
          <Button onClick={handleUploadOpen} variant="contained" component="label" color="primary" sx={{ marginBottom: "10px" }}>Upload Document</Button>
          <UploadDocumentModal assetId={asset.asset.id} open={uploadOpen} handleClose={() => {
            handleUploadClose()
            navigate("")
          }}></UploadDocumentModal>

        </Box>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StyledDataGrid rows={documentrows} columns={documentcolumns} showToolbar />
        </div>
      </Box>

      <Box sx={{
        width: '100%'
      }} >

        <Box display="flex" justifyContent="space-between" alignItems={"flex-end"}>
          <Typography variant="h5">Reminders</Typography>
          <Button onClick={handleAddReminderOpen} variant="contained" component="label" color="primary" sx={{ marginBottom: "10px" }}>Add Reminder</Button>
          <AddReminderModal assetId={asset.asset.id} open={addReminderOpen} handleClose={() => {
            handleAddReminderClose()
            navigate("")
          }}></AddReminderModal>

        </Box>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StyledDataGrid rows={reminderRows} columns={reminderColumns} showToolbar />
        </div>
      </Box>

      <Typography variant='h5' fontWeight={500} fontSize={"1.25rem"}>Audit Events</Typography>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="audit events table">
          <TableHead>
            <TableRow>
              <HeaderTableCell>Timestamp</HeaderTableCell>
              <HeaderTableCell>User</HeaderTableCell>
              <HeaderTableCell>Event</HeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audit_events && audit_events.length > 0 ? (
              audit_events.map((event, idx) => {
                const e = event.asset_audit_event;
                const user = (event.user_first_name || event.user_last_name) ? `${event.user_first_name || ''} ${event.user_last_name || ''}`.trim() : event.user_id;
                return (
                  <TableRow key={idx}>
                    <TableCell>{e.timestamp ? new Date(e.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}</TableCell>
                    <TableCell>{user}</TableCell>
                    <TableCell>{e.audit_description || ''}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow><TableCell colSpan={3}><Alert severity="info">No audit events found</Alert></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  )
}

export default AssetInfo